import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Shield, ChevronRight, Save, RotateCcw, Eye, EyeOff, Layout } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-700 border-red-200',
  sales: 'bg-sky-100 text-sky-700 border-sky-200',
  project_manager: 'bg-violet-100 text-violet-700 border-violet-200',
  foreman: 'bg-amber-100 text-amber-700 border-amber-200',
  permit_tech: 'bg-teal-100 text-teal-700 border-teal-200',
  accounting: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

export default function RoleEditor() {
  const [roles, setRoles] = useState({});
  const [catalog, setCatalog] = useState({ permissions: {}, nav_items: [], roles: {} });
  const [selectedRole, setSelectedRole] = useState(null);
  const [editConfig, setEditConfig] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get('/users/roles').then(r => setRoles(r.data)).catch(() => {});
    api.get('/users/roles/catalog').then(r => setCatalog(r.data)).catch(() => {});
  }, []);

  const selectRole = (key) => {
    setSelectedRole(key);
    setEditConfig({
      permissions: [...(roles[key]?.permissions || [])],
      nav_items: [...(roles[key]?.nav_items || [])],
      hidden_fields: [...(roles[key]?.hidden_fields || [])],
    });
    setDirty(false);
  };

  const togglePermission = (perm) => {
    setEditConfig(prev => {
      const perms = prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm];
      return { ...prev, permissions: perms };
    });
    setDirty(true);
  };

  const toggleNav = (key) => {
    setEditConfig(prev => {
      const items = prev.nav_items.includes(key)
        ? prev.nav_items.filter(n => n !== key)
        : [...prev.nav_items, key];
      return { ...prev, nav_items: items };
    });
    setDirty(true);
  };

  const toggleHiddenField = (field) => {
    setEditConfig(prev => {
      const fields = prev.hidden_fields.includes(field)
        ? prev.hidden_fields.filter(f => f !== field)
        : [...prev.hidden_fields, field];
      return { ...prev, hidden_fields: fields };
    });
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      await api.put(`/users/roles/${selectedRole}`, editConfig);
      toast.success(`${roles[selectedRole]?.label} permissions updated`);
      const r = await api.get('/users/roles');
      setRoles(r.data);
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleReset = async () => {
    selectRole(selectedRole);
    toast.info('Reset to saved state');
  };

  const isAdmin = selectedRole === 'admin';

  return (
    <div className="space-y-6" data-testid="role-editor">
      <div>
        <h2 className="text-lg font-bold text-slate-900">Role Permissions</h2>
        <p className="text-sm text-slate-500">Click a role to customize what they can see and do</p>
      </div>

      {/* Role selector cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {Object.entries(roles).map(([key, role]) => (
          <Card
            key={key}
            className={`cursor-pointer transition-all border-2 ${
              selectedRole === key
                ? `${ROLE_COLORS[key]} shadow-md ring-2 ring-offset-1 ${ROLE_COLORS[key]?.split(' ')[2] || 'ring-sky-200'}`
                : 'border-slate-100 hover:border-slate-200 hover:shadow-sm'
            }`}
            onClick={() => selectRole(key)}
            data-testid={`role-select-${key}`}
          >
            <CardContent className="p-3.5">
              <div className="flex items-center justify-between mb-1">
                <Badge className={`text-[10px] font-bold ${ROLE_COLORS[key]?.split(' ').slice(0, 2).join(' ') || 'bg-slate-100 text-slate-600'}`}>
                  {role.label}
                </Badge>
                {selectedRole === key && <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
              </div>
              <p className="text-[10px] text-slate-500 line-clamp-1">{role.description}</p>
              <div className="flex gap-1 mt-2">
                <span className="text-[9px] text-slate-400">{role.permissions?.length || 0} perms</span>
                <span className="text-[9px] text-slate-300">|</span>
                <span className="text-[9px] text-slate-400">{role.nav_items?.length || 0} pages</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Permission editor */}
      {selectedRole && editConfig && (
        <Card className="border-slate-200 animate-fade-in">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-sky-600" />
                <CardTitle className="text-base">{roles[selectedRole]?.label}</CardTitle>
                {isAdmin && <Badge className="bg-red-100 text-red-600 text-[9px]">Locked</Badge>}
              </div>
              {!isAdmin && (
                <div className="flex gap-2">
                  {dirty && (
                    <Button variant="ghost" size="sm" onClick={handleReset} data-testid="role-reset-btn">
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
                    </Button>
                  )}
                  <Button size="sm" className="bg-sky-500 hover:bg-sky-600 text-white" onClick={handleSave}
                    disabled={!dirty} data-testid="role-save-btn">
                    <Save className="w-3.5 h-3.5 mr-1" /> Save Changes
                  </Button>
                </div>
              )}
            </div>
            {isAdmin && <p className="text-xs text-slate-500 mt-1">Admin role has full access and cannot be modified.</p>}
          </CardHeader>

          {!isAdmin && (
            <CardContent className="space-y-5">
              {/* Page Access */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Layout className="w-4 h-4 text-slate-500" />
                  <h3 className="text-sm font-bold text-slate-700">Page Access</h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {(catalog.nav_items || []).map(nav => (
                    <div
                      key={nav.key}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${
                        editConfig.nav_items.includes(nav.key)
                          ? 'bg-sky-50 border-sky-300 text-sky-800'
                          : 'bg-slate-50 border-slate-200 text-slate-400'
                      }`}
                      onClick={() => toggleNav(nav.key)}
                      data-testid={`nav-toggle-${nav.key}`}
                    >
                      <div className={`w-2 h-2 rounded-full ${editConfig.nav_items.includes(nav.key) ? 'bg-sky-500' : 'bg-slate-300'}`} />
                      <span className="text-xs font-medium">{nav.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Permissions by category */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-4 h-4 text-slate-500" />
                  <h3 className="text-sm font-bold text-slate-700">Permissions</h3>
                </div>
                <div className="space-y-4">
                  {Object.entries(catalog.permissions || {}).map(([category, perms]) => (
                    <div key={category}>
                      <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">{category}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {perms.map(perm => (
                          <div
                            key={perm.key}
                            className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                          >
                            <span className="text-xs text-slate-700">{perm.label}</span>
                            <Switch
                              checked={editConfig.permissions.includes(perm.key)}
                              onCheckedChange={() => togglePermission(perm.key)}
                              className="scale-75"
                              data-testid={`perm-toggle-${perm.key}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Hidden Fields */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <EyeOff className="w-4 h-4 text-slate-500" />
                  <h3 className="text-sm font-bold text-slate-700">Hidden Information</h3>
                  <p className="text-[10px] text-slate-400">(fields this role cannot see)</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {['margin', 'cost', 'markup', 'overhead', 'profit', 'pricing'].map(field => (
                    <div
                      key={field}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${
                        editConfig.hidden_fields.includes(field)
                          ? 'bg-red-50 border-red-300 text-red-700'
                          : 'bg-slate-50 border-slate-200 text-slate-500'
                      }`}
                      onClick={() => toggleHiddenField(field)}
                      data-testid={`hidden-toggle-${field}`}
                    >
                      {editConfig.hidden_fields.includes(field)
                        ? <EyeOff className="w-3 h-3" />
                        : <Eye className="w-3 h-3" />}
                      <span className="text-xs font-medium capitalize">{field}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}
